import { Grid, makeStyles, TextField, Tooltip } from '@material-ui/core';
import { Lock } from '@material-ui/icons';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import ExplanationHover from 'components/ExplanationHover';
import ToggleCard from 'components/ToggleCard';
import { FormikErrors } from 'formik';
import i18n from 'i18n';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import {
  EnvironmentFields,
  ZeroTrustProps,
} from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import AllowList from '../AllowList';
import styles from '../styles';

const useStyles = makeStyles(styles);

interface ZeroTrustCardProps {
  companySelected: CompanyData | null;
  environmentSelected: EnvironmentFields | null;
  isLoading: boolean;
  role?: RolesEnum;
  zeroTrustValues?: ZeroTrustProps;
  zeroTrust?: ZeroTrustProps;
  setFieldValue: (field: string, value: any) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  errors: FormikErrors<any>;
}

const ZeroTrustCard = ({
  companySelected,
  environmentSelected,
  isLoading,
  role,
  zeroTrustValues,
  zeroTrust,
  setFieldValue,
  setHasError,
  errors,
}: ZeroTrustCardProps) => {
  const classes = useStyles();

  const canUseZeroTrust =
    environmentSelected?.user_connection?.network_type === 'public';

  return (
    <ToggleCard
      id="toggle-zero-trust"
      active={!!zeroTrust?.enabled}
      title={i18n.t('Zero Trust')}
      icon={
        <Lock
          className={clsx(classes.iconLock, {
            [classes.iconLockAdmin]: inAdminView(role, companySelected),
            [classes.iconLockSuperAdmin]: inSuperAdminView(
              role,
              companySelected
            ),
            [classes.iconLockOperator]: inOperatorView(role, companySelected),
          })}
        />
      }
      getActive={value => {
        setFieldValue('zero_trust.enabled', !value);
      }}
      disable={!canUseZeroTrust}
      customSubheader={
        !canUseZeroTrust && (
          <Tooltip title={i18n.t('ZERO_TRUST_CONFIG_INFO_TEXT_KEY')}>
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Tooltip>
        )
      }
    >
      <section className={classes.content}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              disabled={!canUseZeroTrust}
              type="number"
              id="port_field"
              onKeyDown={evt =>
                ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
              }
              label={i18n.t('PORT_KEY')}
              variant="outlined"
              value={zeroTrustValues?.port || 33450}
              onChange={({ target }) => {
                const value = Number(target.value);

                setFieldValue(
                  'zero_trust.port',
                  value <= 65536 ? value : 65536
                );
              }}
              InputProps={{
                inputProps: {
                  min: 0,
                  max: 65536,
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <AllowList
              disabled={!canUseZeroTrust}
              onChange={(value: string[]) => {
                setFieldValue('zero_trust.allow_list', value);
              }}
              onError={setHasError}
              value={zeroTrustValues?.allow_list || []}
              loading={isLoading}
            />
            <ExplanationHover
              title={i18n.t('REMOVED_IPS_ZERO_TRUST_EXPLANATION_KEY')}
            />
          </Grid>

          <GenericForm
            fields={{ zero_trust: zeroTrustValues }}
            screenFields={[
              {
                gridXs: 10,
                label: i18n.t(
                  'ZERO_TRUST_CONFIG_WHITELIST_CLEANUP_INTERVAL_KEY'
                ),
                stateKey: 'zero_trust.whitelist_cleanup_interval_days',
                type: 'number',
                validateOnChange: true,
              },
            ]}
            errors={errors}
            setFieldValue={setFieldValue}
          />

          {role === RolesEnum.superAdmin && (
            <Grid item>
              <Alert severity="error" color="info">
                {i18n.t('ALLOWED_SKYONE_IPS_ZERO_TRUST_KEY')}
              </Alert>
            </Grid>
          )}
        </Grid>
      </section>
    </ToggleCard>
  );
};

const mapStateToProps = ({
  environments: { environmentSelected, isLoading },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  zeroTrust: environmentSelected?.zero_trust,
  environmentSelected,
  isLoading,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {})(ZeroTrustCard);
